import { useEffect, useState } from 'react';
import { Stack, TextField, Typography } from '@mui/material';

import Modal from '@/components/ui/Modal';
import CTAButton from '@/components/ui/CTAButton';
import FilterSelect from '@/components/ui/shared/FilterSelect';
import { dsTextFieldSx } from '@/components/ui/shared/filterStyles';
import api from '@/services/api';
import useToastStore from '@/store/toast';
import { TicketArt } from '@/types';

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
}

const MIN_LENGTH = 5;

// Eingabe-Vorlage: User schreibt in die Luecken nach der Frage.
// (Seite/URL wird automatisch mitgesendet, daher keine Seiten-Frage.)
const TEMPLATE = 'Was soll geändert werden?\n\n';

/**
 * „Ticket erstellen" (Punkt 6 der Aufnahme vom 09.09.2026, Auftrag Alex
 * 10.09.). Hiess bis dahin „Feedback" und sass in einem eigenen schwebenden
 * Knopf; seit dem 10.09. ist es ein Eintrag im gemeinsamen FAB-Menue.
 *
 * ── DIE ART IST PFLICHT (Entscheid Alex 10.09.2026) ──────────────────────
 * Bis dahin war sie freiwillig und ein Ticket ohne Angabe lief als
 * „Allgemein" durch. Jetzt ordnet ein, wer meldet: **keine Vorauswahl und
 * keine „Allgemein"-Option** — der Platzhalter heisst „Bitte wählen" und ist
 * kein gueltiger Wert. Das Backend prueft dasselbe (`required` in
 * `CreateFeedbackRequest`); die Pruefung hier erspart nur den Rundlauf.
 *
 * ⚠️ Der BESTAND ist davon unberuehrt: Tickets mit `art = null` stammen aus
 * der Zeit davor und zeigen in der Triage weiterhin „Allgemein". Das ist
 * Anzeige, kein Wert — deshalb gibt es hier auch keine Option dafuer.
 *
 * ── DIE LISTE KOMMT VOM SERVER ───────────────────────────────────────────
 * Sie wird NICHT hier hartkodiert: „konfigurierbar" hiesse sonst faktisch
 * „Deploy" (Frage 6.1 der Aufnahme).
 *
 * 🔥 Faellt der Abruf aus ODER ist keine Art gepflegt, gibt es nichts zu
 * waehlen — und weil die Wahl Pflicht ist, kann dann NICHT gemeldet werden.
 * Genau dieser Fall bekommt eine ausgesprochene Meldung und einen
 * abgeschalteten Senden-Knopf (Vorgabe Alex: „klare Meldung statt stillem
 * Fehler"). Vorher lief er als leeres Dropdown durch, was aussah wie ein
 * Anzeigefehler und keiner war.
 */
const FeedbackModal = ({ open, onClose }: FeedbackModalProps) => {
  const [message, setMessage] = useState(TEMPLATE);
  const [art, setArt] = useState<string>('');
  const [arten, setArten] = useState<TicketArt[]>([]);
  // `null` = noch nicht geladen. Der Unterschied zu „geladen, aber leer"
  // entscheidet, ob wir „wird geladen" oder „keine Art gepflegt" zeigen —
  // ein stiller Fehler waere hier das Schlechteste.
  const [artenGeladen, setArtenGeladen] = useState(false);
  const [artFehler, setArtFehler] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const showToast = useToastStore((s) => s.showToast);

  useEffect(() => {
    if (!open) return;

    let verworfen = false;

    api.feedback
      .getTicketArten()
      .then((res) => {
        if (verworfen) return;
        // Nur waehlbare Arten: eine abgeschaltete darf nicht mehr neu
        // vergeben werden. Das Backend lehnt sie ohnehin ab (422) — sie gar
        // nicht erst anzubieten ist die freundlichere Haelfte davon.
        setArten((res.data ?? []).filter((a) => a.is_active));
        setArtenGeladen(true);
      })
      .catch(() => {
        if (verworfen) return;
        // 🔥 NICHT mehr still: seit die Art Pflicht ist, kann ohne Liste gar
        // nicht gemeldet werden. Ein leeres Dropdown ohne Erklaerung waere
        // eine Sackgasse — der Nutzer soll wissen, woran es liegt.
        setArten([]);
        setArtenGeladen(true);
      });

    return () => {
      verworfen = true;
    };
  }, [open]);

  // „Geladen und trotzdem leer" ist der Fall, der die Meldung ausloest —
  // waehrend des Ladens waere sie ein Fehlalarm.
  const keineArtenGepflegt = artenGeladen && arten.length === 0;

  const handleClose = () => {
    if (submitting) return;
    setMessage(TEMPLATE);
    setArt('');
    setError(null);
    setArtFehler(false);
    onClose();
  };

  const handleSubmit = async () => {
    // Die Art zuerst: sie steht oben im Fenster, und der Hinweis soll dort
    // erscheinen, wo die fehlende Angabe ist.
    if (!art) {
      setArtFehler(true);
      setError('Bitte wähle eine Art.');
      return;
    }

    const trimmed = message.trim();
    // Nackte Vorlage (keine eigene Eingabe) nicht senden.
    if (trimmed === TEMPLATE.trim() || trimmed.length === 0) {
      setError('Bitte deine Eingabe ergänzen.');
      return;
    }
    if (trimmed.length < MIN_LENGTH) {
      setError(`Bitte gib mindestens ${MIN_LENGTH} Zeichen ein.`);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await api.feedback.createFeedback({
        message: trimmed,
        // 🔥 Die Herkunft: die volle Adresse der Seite, auf der gemeldet
        // wurde — inklusive Case- bzw. Kunden-ID im Pfad. `window.location`
        // ist global, das Fenster liest also die AKTUELLE Seite, auch wenn es
        // seit dem FAB-Umzug (10.09.2026) im Provider haengt und nicht mehr
        // neben dem Knopf. Ein Test je Einstiegspunkt klammert das.
        url: window.location.href,
        art,
      });
      showToast('Danke für dein Ticket', 'success');
      setMessage(TEMPLATE);
      setArt('');
      setArtFehler(false);
      onClose();
    } catch {
      setError('Senden fehlgeschlagen. Bitte versuche es erneut.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Ticket erstellen"
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
    >
      <Stack gap={4}>
        <Stack gap={1}>
          <Typography variant="label" component="p">Art *</Typography>
          <FilterSelect
            aria-label="Ticket-Art"
            options={arten.map((a) => ({ value: a.slug, label: a.name }))}
            // „Bitte wählen" ist der Platzhalter, KEIN gueltiger Wert — es gibt
            // seit dem 10.09.2026 keine „Allgemein"-Option mehr.
            placeholderOption={{ value: '', label: 'Bitte wählen' }}
            value={art}
            onChange={(wert) => { setArt(String(wert)); setArtFehler(false); setError(null); }}
            disabled={submitting || keineArtenGepflegt}
            error={artFehler}
            fullWidth
          />
        </Stack>
        {keineArtenGepflegt && (
          <Typography variant="meta" sx={{ color: 'error.main' }}>
            Es ist keine Ticket-Art gepflegt — ohne Art lässt sich kein Ticket
            erstellen. Bitte wende dich an einen Administrator (Benutzer &amp;
            Teams → Ticket-Arten).
          </Typography>
        )}
        <TextField
          autoFocus
          multiline
          minRows={4}
          fullWidth
          sx={dsTextFieldSx}
          placeholder="Was möchtest du uns mitteilen?"
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            if (error) setError(null);
          }}
          error={!!error}
          disabled={submitting}
        />
        {!!error && (
          <Typography variant="meta" sx={{ color: 'error.main' }}>
            {error}
          </Typography>
        )}
        <Stack direction="row" gap={2} justifyContent="flex-end">
          <CTAButton
            label="Abbrechen"
            variant="tertiary"
            onClick={handleClose}
            disabled={submitting}
            autoWidth
          />
          <CTAButton
            label="Senden"
            variant="secondary"
            onClick={handleSubmit}
            loading={submitting}
            disabled={keineArtenGepflegt}
            autoWidth
          />
        </Stack>
      </Stack>
    </Modal>
  );
};

export default FeedbackModal;
